/**
 * Entry-scoped map update, so undoing one message's feedback never disturbs another's — the vote
 * and comment maps are shared by the whole conversation. `undefined` drops the key rather than
 * storing an empty value, which is what `submit` reads to decide there is nothing to send.
 */
export function withEntry<T>(entries: Record<string, T>, key: string, value: T | undefined): Record<string, T> {
  if (value === undefined) {
    const { [key]: _dropped, ...rest } = entries;

    return rest;
  }

  return { ...entries, [key]: value };
}

/**
 * Per-message write counter. Nothing serializes the thumbs — `CopilotFeedback` is rendered without
 * `disabled` and its handlers do not await — so two writes for one message can overlap. `submit`
 * sends that message's whole current state, so once a newer write has landed an older failure says
 * nothing about what the backend holds: only the newest write for a message may roll itself back.
 */
export function createCopilotWriteLedger() {
  const writes = new Map<string, number>();

  /** Claims the next write number for a message. */
  function record(messageId: string): number {
    const write = (writes.get(messageId) ?? 0) + 1;
    writes.set(messageId, write);

    return write;
  }

  /** Whether that write is still the message's most recent one — the only one allowed to undo. */
  function isLatest(messageId: string, write: number): boolean {
    return writes.get(messageId) === write;
  }

  /** "Nouvelle discussion": the messages are gone, so no write may roll anything back afterwards. */
  function clear(): void {
    writes.clear();
  }

  return { record, isLatest, clear };
}
